<template>
  <div>
    <!-- Search & Filters Bar -->
    <div class="search-bar">
      <div class="search-bar__input-wrap">
        <input
          type="text"
          v-model="searchQuery"
          @input="debounceSearch"
          :placeholder="'Search ' + sectionLabel + '...'"
          class="search-bar__input"
        />
        <svg class="search-bar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
        </svg>
      </div>

      <div class="search-bar__filters">
        <!-- Sort -->
        <select v-model="sortBy" @change="fetchPartners(1)" class="filter-select">
          <option value="popular">Most Popular</option>
          <option value="newest">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
          <option value="rating">Highest Rated</option>
          <option value="title">A - Z</option>
        </select>
      </div>
    </div>

    <!-- Results Count -->
    <div class="search-results-info" v-if="totalResults > 0">
      <span class="search-results-info__count">{{ totalResults.toLocaleString() }} results</span>
      <span v-if="searchQuery" class="search-results-info__query">for "{{ searchQuery }}"</span>
    </div>

    <!-- Loading -->
    <div v-if="loading && partners.length === 0" class="section-grid">
      <div v-for="n in 8" :key="'sk-' + n" class="section-card section-card--skeleton">
        <div class="section-card__image"></div>
        <div class="section-card__content">
          <div class="skeleton-line skeleton-line--title"></div>
          <div class="skeleton-line skeleton-line--subtitle"></div>
          <div class="skeleton-line skeleton-line--meta"></div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div v-else-if="!loading && partners.length === 0" class="search-empty">
      <p>No results found.</p>
      <button @click="clearFilters" class="btn-load-more">Clear Filters</button>
    </div>

    <!-- Results Grid -->
    <div v-else class="section-grid">
      <PartnerCard v-for="p in partners" :key="p.id" :partner="p" @clicked="trackClick(p)" />
    </div>

    <!-- Pagination -->
    <div v-if="lastPage > 1" class="pagination">
      <button
        class="pagination__btn"
        :disabled="currentPage <= 1"
        @click="fetchPartners(currentPage - 1)"
      >&laquo; Prev</button>

      <template v-for="page in visiblePages">
        <span v-if="page === '...'" :key="'dot-' + page" class="pagination__dots">...</span>
        <button
          v-else
          :key="page"
          :class="['pagination__btn', { 'pagination__btn--active': page === currentPage }]"
          @click="fetchPartners(page)"
        >{{ page }}</button>
      </template>

      <button
        class="pagination__btn"
        :disabled="currentPage >= lastPage"
        @click="fetchPartners(currentPage + 1)"
      >Next &raquo;</button>
    </div>
  </div>
</template>

<script>
import PartnerCard from './PartnerCard.vue'

export default {
  components: { PartnerCard },
  props: {
    section: { type: String, required: true },
    initialPartners: { type: Array, default: () => [] },
    perPage: { type: Number, default: 20 }
  },
  data() {
    return {
      partners: this.initialPartners,
      loading: false,
      searchQuery: '',
      sortBy: 'popular',
      currentPage: 1,
      lastPage: 1,
      totalResults: this.initialPartners.length,
      searchTimeout: null,
      initialized: false,
    }
  },
  computed: {
    sectionLabel() {
      return this.section === 'activities' ? 'activities & experiences' : 'travel & destinations'
    },
    visiblePages() {
      const pages = []
      const total = this.lastPage
      const current = this.currentPage

      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
        return pages
      }

      pages.push(1)
      if (current > 3) pages.push('...')

      const start = Math.max(2, current - 1)
      const end = Math.min(total - 1, current + 1)
      for (let i = start; i <= end; i++) pages.push(i)

      if (current < total - 2) pages.push('...')
      pages.push(total)

      return pages
    }
  },
  mounted() {
    this.fetchPartners(1)
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.fetchPartners(1)
      }, 400)
    },
    clearFilters() {
      this.searchQuery = ''
      this.sortBy = 'popular'
      this.fetchPartners(1)
    },
    async fetchPartners(page) {
      this.loading = true
      this.currentPage = page

      const params = new URLSearchParams({
        page: page,
        per_page: this.perPage,
        section: this.section,
        sort: this.sortBy,
      })

      if (this.searchQuery) params.set('q', this.searchQuery)

      try {
        const res = await fetch(`/api/partners?${params}`)
        const data = await res.json()
        this.partners = data.data || []
        this.currentPage = data.current_page
        this.lastPage = data.last_page
        this.totalResults = data.total
      } catch (e) {
        console.error('Failed to fetch partners', e)
      }

      this.loading = false
      if (this.initialized) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
      this.initialized = true
    },
    trackClick(partner) {
      if (window.mixpanel) {
        mixpanel.track('partner_card_clicked', {
          partner_id: partner.id, type: partner.type, source: partner.source,
          category: partner.category, title: partner.title
        })
      }
    }
  }
}
</script>
